<template>
  <div v-if="!loading">
    <auth-layout
      v-if="onboardingKeyVerified && !onboardingComplete"
      pageClass="onboarding-page"
    >
      <div class="onboarding-container">
        <div class="onboarding-header-content">
          <div class="header-section">
            <div class="header-container">
              <h2>Hilo Onboarding</h2>
              <img
                src="/static/img/hilo-transparent-background-black-icon.png"
                alt="Hilo Logo"
                class="onboarding-logo"
              />
            </div>
            <h4>Required Information</h4>
          </div>
          <div class="profile-section">
            <div class="profile-photo-upload-container">
              <img
                :src="profileImage"
                alt="Profile Photo"
                class="profile-photo-preview"
              />
              <FileUpload
                label="Profile Photo"
                fieldId="image"
                v-model="formData.image"
                :isRequired="imageRequired"
                :showFileName="false"
                @upload-success="handleImageUploadSuccess"
              />
            </div>
          </div>
        </div>
        <form @submit.prevent="submitForm" class="form-container">
          <AddEditFormGroup
            label="First Name"
            fieldId="firstName"
            v-model="formData.firstName"
            required
          />
          <AddEditFormGroup
            label="Last Name"
            fieldId="lastName"
            v-model="formData.lastName"
            required
          />
          <AddEditFormGroup
            label="Email"
            fieldId="email"
            v-model="formData.email"
            type="email"
            required
          />
          <AddEditFormGroup
            label="Phone"
            fieldId="phone"
            v-model="formData.phone"
            type="tel"
            required
          />
          <AddEditFormGroup
            label="Date of Birth"
            fieldId="dob"
            v-model="formData.dob"
            type="date"
            required
          />
          <AddEditFormGroup
            label="Gender"
            fieldId="genderId"
            v-model="formData.genderId"
            component="select"
            :options="genderOptions"
            required
          />
          <AddEditFormGroup
            label="Address1"
            fieldId="address1"
            v-model="formData.address1"
            required
          />
          <AddEditFormGroup
            label="Address2"
            fieldId="address2"
            v-model="formData.address2"
          />
          <AddEditFormGroup
            label="City"
            fieldId="city"
            v-model="formData.city"
            required
          />
          <AddEditFormGroup
            label="State"
            fieldId="state"
            v-model="formData.state"
            required
          />
          <AddEditFormGroup
            label="Zip"
            fieldId="zip"
            v-model="formData.zip"
            required
          />
          <AddEditFormGroup
            label="Certificate Number"
            fieldId="cert"
            v-model="formData.cert"
            required
          />
          <FileUpload
            label="Photo ID (Passport or Drivers License)"
            fieldId="photoId"
            v-model="formData.photoId"
            :isRequired="imageRequired"
          />
          <FileUpload
            v-if="userType === 'Potential Instructor'"
            label="Commercial Pilot Cert (FAA)"
            fieldId="commercialCert"
            v-model="formData.commercialCert"
            :isRequired="imageRequired"
          />
          <FileUpload
            v-if="userType === 'Potential Instructor'"
            label="CFI Certificate"
            fieldId="cfiCert"
            v-model="formData.cfiCert"
            :isRequired="imageRequired"
          />
          <FileUpload
            label="Medical Certificate / Basic Med (FAA)"
            fieldId="medicalCert"
            v-model="formData.medicalCert"
            :isRequired="imageRequired"
          />
          <FileUpload
            label="TSA Citizenship Verification (Birth Certificate)"
            fieldId="birthCert"
            v-model="formData.birthCert"
            :isRequired="imageRequired"
          />
          <div
            v-if="userType === 'Potential Students'"
            class="form-group signature-section"
          >
            <hilo-button label="View Contract" @click="openViewContractModal" />
            <hilo-button label="Sign Contract" @click="openSignatureModal" />
            <div v-if="formData.studentSignature" class="signature-display">
              <span class="signature-date">Signature: </span>
              <img
                :src="formData.studentSignature"
                alt="Student Signature"
                class="saved-signature"
              />
              <div class="signature-date">
                Date: {{ formattedSignatureDate }}
              </div>
            </div>
            <span
              v-if="imageRequired && !formData.studentSignature"
              class="error-message"
              >Required</span
            >
            <span v-else-if="formData.studentSignature" class="success-message"
              >✔</span
            >
          </div>
          <HiloModal
            :isVisible="viewContractModalVisible"
            title="View Contract"
            @close="viewContractModalVisible = false"
          >
            <template v-slot:default>
              <object
                :data="formData.contractImage"
                type="application/pdf"
                class="contract-pdf"
                width="100%"
                height="600px"
              >
                This browser does not support PDFs. Please download the PDF to
                view it:
                <a :href="formData.contractImage">Download PDF</a>
              </object>
            </template>
          </HiloModal>
          <SignatureModal
            :isVisible="signatureModalVisible"
            :firstName="formData.firstName"
            :lastName="formData.lastName"
            @save="handleSignatureSave"
            @close="signatureModalVisible = false"
          />
          <div class="form-group full-width">
            <hilo-button type="submit" styleType="submit" label="Complete" />
          </div>
        </form>
      </div>
    </auth-layout>
    <auth-layout
      v-else-if="onboardingComplete"
      pageClass="onboarding-complete-page"
    >
      <div class="onboarding-complete">
        <h3>Congratulations!</h3>
        <h3>You have successfully completed your onboarding process.</h3>
        <h3>
          An admin will review your information and be in touch with you within
          a couple of days.
        </h3>
      </div>
    </auth-layout>
    <NotFoundPage v-else />
  </div>
</template>

<script>
import AuthLayout from "../Pages/AuthLayout.vue";
import HiloButton from "../../../components/HiloComponents/HiloButton.vue";
import AddEditFormGroup from "./AddEditPages/AddEditFormGroup.vue";
import FileUpload from "../../../components/HiloComponents/FileUpload.vue";
import NotFoundPage from "../../GeneralViews/NotFoundPage.vue";
import SignatureModal from "../../../components/HiloComponents/SignatureModal.vue";
import HiloModal from "../../../components/HiloComponents/HiloModal.vue";
import { PDFDocument } from "pdf-lib";
import dayjs from "dayjs";
import UsersAPIService from "src/servicehandlers/UsersAPIService";

const usersAPIService = new UsersAPIService();

export default {
  components: {
    HiloModal,
    SignatureModal,
    NotFoundPage,
    FileUpload,
    AddEditFormGroup,
    HiloButton,
    AuthLayout,
  },
  data() {
    return {
      formData: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        dob: "",
        genderId: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        cert: "",
        image: "",
        photoId: "",
        commercialCert: "",
        cfiCert: "",
        medicalCert: "",
        birthCert: "",
        contractImage: "",
        studentSignature: "",
        studentSignatureDate: "",
      },
      loading: true,
      genderOptions: [],
      defaultImage: "/static/img/faces/face-0.jpg",
      profileImage: "",
      imageRequired: false,
      instructorUserType: "",
      onboardingKeyVerified: false,
      viewContractModalVisible: false,
      signatureModalVisible: false,
      onboardingComplete: false,
      userType: "Potential Students",
    };
  },
  computed: {
    formattedSignatureDate() {
      return dayjs(this.formData.studentSignatureDate).format("MM/DD/YYYY");
    },
  },
  async created() {
    await this.getUser(this.$route.params.id);
    await this.getGenderOptions();
    await this.getInstructorUserTypeId();
  },
  methods: {
    async getUser(id) {
      try {
        this.loading = true;
        this.formData = await usersAPIService.getOnboardingById(
          id,
          this.$route.params.onboardingKey,
        );
        const instructorID = await this.getInstructorUserTypeId();
        if (this.formData.userTypeId === instructorID) {
          this.userType = "Potential Instructor";
        }
        if (this.formData.image && typeof this.formData.image === "string") {
          this.formData.image = `${process.env.VUE_APP_API_URL}/file_uploads/${this.formData.image.split("/").pop()}`;
        }
        this.profileImage = this.formData.image || this.defaultImage;

        this.formData.photoId = this.formData.onboarding?.photoId;
        this.formData.commercialCert = this.formData.onboarding?.commercialCert;
        this.formData.cfiCert = this.formData.onboarding?.cfiCert;
        this.formData.medicalCert = this.formData.onboarding?.medicalCert;
        this.formData.birthCert = this.formData.onboarding?.birthCert;

        if (
          this.formData.potentialStudent?.quote?.studentSignature &&
          typeof this.formData.potentialStudent?.quote?.studentSignature ===
            "string"
        ) {
          this.formData.studentSignature = `${process.env.VUE_APP_API_URL}/file_uploads/${this.formData.potentialStudent.quote.studentSignature.split("/").pop()}`;
        }

        if (
          this.formData.potentialStudent?.quote?.image &&
          typeof this.formData.potentialStudent?.quote?.image === "string"
        ) {
          this.formData.contractImage = `${process.env.VUE_APP_API_URL}/file_uploads/${this.formData.potentialStudent.quote.image.split("/").pop()}`;
        }
      } catch (error) {}

      if (this.$route.params.onboardingKey === this.formData.onboardingKey) {
        this.onboardingKeyVerified = true;
      }
      this.loading = false;
    },
    async getGenderOptions() {
      try {
        this.genderOptions = await usersAPIService.getGenders();
      } catch (error) {}
    },
    async getInstructorUserTypeId() {
      try {
        const userTypes = await usersAPIService.getUserTypes();
        const instructorType = userTypes.find(
          (type) => type.name === "Instructor",
        );
        this.instructorUserType = instructorType ? instructorType.id : null;
        return this.instructorUserType;
      } catch (error) {
        this.instructorUserType = null;
      }
    },
    async handleImageUploadSuccess(file) {
      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await usersAPIService.updateOnboarding(
          this.$route.params.id,
          formData,
        );
        if (response.image && typeof response.image === "string") {
          this.formData.image = `${process.env.VUE_APP_API_URL}/file_uploads/${response.image.split("/").pop()}`;
        }
        this.profileImage = this.formData.image || this.defaultImage;
      } catch (error) {}
    },
    openViewContractModal() {
      this.viewContractModalVisible = true;
    },
    openSignatureModal() {
      this.signatureModalVisible = true;
    },
    handleSignatureSave(studentSignature) {
      this.formData.studentSignature = studentSignature;
      this.formData.studentSignatureDate = new Date();
    },
    async addSignatureAndDateToContract() {
      try {
        const existingPdfBytes = await fetch(this.formData.contractImage).then(
          (res) => res.arrayBuffer(),
        );
        const signatureImageBytes = await fetch(
          this.formData.studentSignature,
        ).then((res) => res.arrayBuffer());

        const pdfDoc = await PDFDocument.load(existingPdfBytes);

        const signatureImage = await pdfDoc.embedPng(signatureImageBytes);
        const { width, height } = signatureImage.scale(0.1);

        const pages = pdfDoc.getPages();
        const firstPage = pages[0];

        const signatureX = 170;
        const signatureY = 165;

        firstPage.drawImage(signatureImage, {
          x: signatureX,
          y: signatureY,
          width: width,
          height: height,
        });

        const signatureDateX = 370;
        const signatureDateY = 170;

        firstPage.drawText(
          dayjs(this.formData.studentSignatureDate).format("MM/DD/YYYY"),
          {
            x: signatureDateX,
            y: signatureDateY,
            size: 8,
          },
        );

        const modifiedPdfBytes = await pdfDoc.save();

        const modifiedPdfBlob = new Blob([modifiedPdfBytes], {
          type: "application/pdf",
        });

        this.formData.contractImage = new File(
          [modifiedPdfBlob],
          `contract_${Date.now()}.pdf`,
          {
            type: "application/pdf",
          },
        );
      } catch (error) {
        throw error;
      }
    },
    async submitForm() {
      if (
        this.formData.image === this.defaultImage ||
        !this.formData.photoId ||
        (this.formData.userTypeId === this.instructorUserType &&
          !this.formData.commercialCert) ||
        (this.formData.userTypeId === this.instructorUserType &&
          !this.formData.cfiCert) ||
        !this.formData.medicalCert ||
        !this.formData.birthCert ||
        (this.formData.userTypeId !== this.instructorUserType &&
          !this.formData.studentSignature)
      ) {
        this.imageRequired = true;
        return;
      }

      try {
        if (this.formData.userTypeId !== this.instructorUserType) {
          await this.addSignatureAndDateToContract();
        }
        await usersAPIService.updateOnboarding(
          this.$route.params.id,
          this.formData,
        );
        await this.getUser(this.$route.params.id);
        this.onboardingComplete = true;
      } catch (error) {}
    },
  },
};
</script>

<style>
.onboarding-container {
  background-color: white;
  padding: 30px;
  margin: 20px 0 20px -100px;
  width: 120%;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.onboarding-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 10px;
}

.header-section {
  flex: 1;
}

.header-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.onboarding-logo {
  width: 40px;
  height: auto;
  margin-top: 10px;
}

.profile-section {
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.profile-photo-upload-container {
  display: flex;
  align-items: center;
  gap: 20px;
}

.profile-photo-preview {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ccc;
  margin-top: 15px;
}

.form-container {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
}

.form-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.form-group.full-width {
  grid-column: span 3;
}

.onboarding-complete {
  background-color: white;
  color: black;
  text-align: center;
  padding: 20px;
  border-radius: 8px;
}

.signature-section {
  display: flex;
  align-items: center;
  gap: 20px;
  height: 38px;
  box-sizing: border-box;
  grid-column: span 2;
}

.saved-signature {
  max-height: 30px;
  border-radius: 5px;
}

.signature-display {
  display: flex;
  align-items: center;
  gap: 10px;
}

.signature-date {
  font-size: 14px;
  color: #555;
}

.error-message,
.success-message {
  margin-left: 10px;
  font-size: 16px;
}

.error-message {
  color: red;
}

.success-message {
  color: green;
}
</style>
